import { useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Clock, X } from 'lucide-react';
import {
  BoutonSecondaire,
  Carte,
  Chargement,
  MessageErreur,
  Pastille,
} from '../ui/Primitives';
import { useAssiduite, type LigneAssiduite } from '../../lib/useAssiduite';
import { ErreurApi } from '../../lib/api';

/**
 * Assiduité du dossier, dans le dossier (CDC §6.6, §6.7).
 *
 * La question à laquelle elle répond n'est pas « qui a dépassé le seuil
 * aujourd'hui » — c'est l'écran des absences qui la traite — mais **cette
 * personne vient-elle ?**, posée devant un dossier, avant d'établir un contrat
 * ou de répondre à un employeur.
 *
 * Elle montre donc les présences autant que les absences. Une liste d'absences
 * seule ne dit ni le rythme, ni la part : trois absences en six mois et trois
 * absences en deux semaines y ont la même tête. D'où les deux lectures côte à
 * côte — le **cumul depuis le début**, qui décide du courrier d'avertissement, et
 * la **tendance du dernier mois**, qui signale un décrochage avant qu'il coûte le
 * contrat.
 *
 * Le détail est replié : ouvrir un dossier, ce n'est pas vouloir relire soixante
 * demi-journées.
 */

function tonTaux(taux: number | null): string {
  if (taux === null) return 'neutral';
  if (taux >= 90) return 'success';
  if (taux >= 75) return 'warning';
  return 'danger';
}

function pourcentage(taux: number | null): string {
  return taux === null ? '—' : `${String(taux).replace('.', ',')} %`;
}

function IconeLigne({ ligne }: { ligne: LigneAssiduite }): JSX.Element {
  if (ligne.statut === 'ABSENT') {
    return (
      <X
        className={`h-4 w-4 shrink-0 ${ligne.justifiee ? 'text-amber-500' : 'text-red-500'}`}
        aria-hidden="true"
      />
    );
  }
  if (ligne.statut === 'RETARD') {
    return <Clock className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />;
  }
  if (ligne.statut === 'ATTENDU') {
    return <Clock className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden="true" />;
  }
  return <CheckCircle2 className="h-4 w-4 shrink-0 text-codialis-mint" aria-hidden="true" />;
}

function Chiffre({
  libelle,
  valeur,
  detail,
  ton = 'text-codialis-navy',
}: {
  libelle: string;
  valeur: string;
  detail?: string;
  ton?: string;
}): JSX.Element {
  return (
    <div className="min-w-0 rounded-2xl bg-codialis-sand p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-codialis-gray">
        {libelle}
      </div>
      <div className={`font-display mt-0.5 text-2xl font-extrabold ${ton}`}>{valeur}</div>
      {detail && <div className="mt-0.5 text-[11px] text-codialis-gray">{detail}</div>}
    </div>
  );
}

export function CarteAssiduite({ apprenantId }: { apprenantId: string }): JSX.Element {
  const { data, isLoading, error } = useAssiduite(apprenantId);
  const [detailOuvert, setDetailOuvert] = useState(false);

  if (isLoading) {
    return (
      <Carte titre="Assiduité" sansBordure>
        <Chargement libelle="Lecture de l’émargement…" />
      </Carte>
    );
  }

  if (error) {
    return (
      <Carte titre="Assiduité" sansBordure>
        <div className="p-4 sm:p-6">
          <MessageErreur
            message={
              error instanceof ErreurApi
                ? error.message
                : 'L’assiduité n’a pas pu être chargée.'
            }
          />
        </div>
      </Carte>
    );
  }

  if (!data) return <></>;

  const { synthese, cumul, tendance } = data;

  // Aucune demi-journée convoquée : le dire, plutôt que d'afficher des zéros qui
  // se lisent comme une absence totale.
  if (synthese.convoquees === 0) {
    return (
      <Carte titre="Assiduité" sansBordure>
        <p className="p-4 text-sm text-codialis-gray sm:p-6">
          Aucune demi-journée d’émargement pour ce dossier. L’assiduité apparaîtra dès la
          première séance tenue.
        </p>
      </Carte>
    );
  }

  return (
    <Carte
      titre="Assiduité"
      actions={
        <>
          <Pastille
            texte={`Présence ${pourcentage(synthese.tauxPresence)}`}
            ton={tonTaux(synthese.tauxPresence)}
          />
          {cumul.seuilDepasse && <Pastille texte="Seuil dépassé" ton="danger" />}
          {data.absenteisme?.auDelaDuSeuil && <Pastille texte="Absentéisme au-delà du seuil" ton="danger" />}
          <BoutonSecondaire onClick={() => setDetailOuvert((ouvert) => !ouvert)}>
            {detailOuvert ? (
              <ChevronDown className="mr-1.5 h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="mr-1.5 h-4 w-4" aria-hidden="true" />
            )}
            {detailOuvert ? 'Replier le détail' : 'Voir le détail'}
          </BoutonSecondaire>
        </>
      }
      sansBordure
    >
      <div className="space-y-4 p-4 sm:p-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Chiffre
            libelle="Taux de présence"
            valeur={pourcentage(synthese.tauxPresence)}
            detail={`sur ${synthese.emargees} demi-journée${synthese.emargees > 1 ? 's' : ''} close${synthese.emargees > 1 ? 's' : ''}`}
          />
          <Chiffre
            libelle="Présences"
            valeur={String(synthese.presents + synthese.retards)}
            detail={
              synthese.retards > 0
                ? `dont ${synthese.retards} retard${synthese.retards > 1 ? 's' : ''}`
                : 'demi-journées'
            }
          />
          <Chiffre
            libelle="Absences"
            valeur={String(synthese.absences)}
            detail={
              synthese.absencesJustifiees > 0
                ? `dont ${synthese.absencesJustifiees} justifiée${synthese.absencesJustifiees > 1 ? 's' : ''}`
                : 'demi-journées'
            }
            ton={synthese.absences > 0 ? 'text-red-600' : 'text-codialis-navy'}
          />
          <Chiffre
            libelle={`Dernier mois`}
            valeur={pourcentage(tendance.tauxPresence)}
            detail={
              tendance.emargees === 0
                ? 'aucune séance sur la période'
                : `${tendance.absences} absence${tendance.absences > 1 ? 's' : ''} sur ${tendance.emargees} demi-journée${tendance.emargees > 1 ? 's' : ''}`
            }
            ton={
              tendance.tauxPresence !== null && tendance.tauxPresence < 75
                ? 'text-red-600'
                : 'text-codialis-navy'
            }
          />
        </div>

        {/* Le cumul décide du courrier d'avertissement : il se lit avec sa marge,
            pas seulement avec son total. Seules les absences injustifiées comptent. */}
        <div
          className={`flex items-start gap-3 rounded-2xl p-3 text-sm ${
            cumul.seuilDepasse ? 'bg-red-50 text-red-900' : 'bg-gray-50 text-codialis-navy'
          }`}
        >
          {cumul.seuilDepasse && (
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
          )}
          <p>
            <strong>
              {cumul.injustifiees} demi-journée{cumul.injustifiees > 1 ? 's' : ''} d’absence
              injustifiée{cumul.injustifiees > 1 ? 's' : ''}
            </strong>{' '}
            pour un seuil de {cumul.seuil}.{' '}
            {cumul.seuilDepasse
              ? 'Le courrier d’avertissement est déclenché depuis l’écran « Absences & avertissements ».'
              : `Encore ${cumul.margeAvantSeuil} avant déclenchement du courrier.`}
            {cumul.justifiees > 0 && (
              <>
                {' '}
                {cumul.justifiees} absence{cumul.justifiees > 1 ? 's' : ''} justifiée
                {cumul.justifiees > 1 ? 's' : ''} ne compte
                {cumul.justifiees > 1 ? 'nt' : ''} pas au seuil.
              </>
            )}
          </p>
        </div>

        {/* Taux d'absentéisme (TF-461) : la règle des apprenants à risque, sur
            les feuilles clôturées seules, absences justifiées comprises. */}
        {data.absenteisme && data.absenteisme.demiJournees > 0 && (
          <p
            className={`rounded-2xl p-3 text-sm ${
              data.absenteisme.auDelaDuSeuil ? 'bg-red-50 text-red-900' : 'bg-gray-50 text-codialis-navy'
            }`}
          >
            <strong>Absentéisme {pourcentage(data.absenteisme.taux)}</strong> ·{' '}
            {data.absenteisme.absences} absence{data.absenteisme.absences > 1 ? 's' : ''} sur{' '}
            {data.absenteisme.demiJournees} demi-journée{data.absenteisme.demiJournees > 1 ? 's' : ''} des feuilles
            clôturées, justifiées comprises · seuil de l’organisme {data.absenteisme.seuil} %.
          </p>
        )}

        {data.journeesAbsentes.length > 0 && (
          <div>
            <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Journées manquées
            </h4>
            <div className="flex flex-wrap gap-2">
              {data.journeesAbsentes.slice(0, 12).map((journee) => (
                <Pastille
                  key={`${journee.jour}-${journee.type}`}
                  texte={`${journee.jourLisible}${
                    journee.type === 'JOURNEE_COMPLETE'
                      ? ''
                      : journee.type === 'MATIN'
                        ? ' · matin'
                        : ' · après-midi'
                  }${journee.justifiee ? ' · justifiée' : ''}`}
                  ton={journee.justifiee ? 'warning' : 'danger'}
                />
              ))}
              {data.journeesAbsentes.length > 12 && (
                <span className="text-xs text-codialis-gray">
                  et {data.journeesAbsentes.length - 12} autre
                  {data.journeesAbsentes.length - 12 > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        )}

        {synthese.attendues > 0 && (
          <p className="text-[11px] text-codialis-gray">
            {synthese.attendues} demi-journée{synthese.attendues > 1 ? 's' : ''} encore
            attendue{synthese.attendues > 1 ? 's' : ''} : elle
            {synthese.attendues > 1 ? 's' : ''} ne compte
            {synthese.attendues > 1 ? 'nt' : ''} pas dans le taux tant que la séance n’est pas
            clôturée.
          </p>
        )}

        {detailOuvert && (
          <div>
            <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Historique des demi-journées
            </h4>
            <ul className="divide-y divide-gray-100">
              {data.lignes.map((ligne) => (
                <li
                  key={ligne.presenceId}
                  className="flex flex-wrap items-center justify-between gap-2 py-2.5"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <IconeLigne ligne={ligne} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-codialis-navy">
                        {ligne.jourLisible} · {ligne.demiJourneeLibelle}
                      </p>
                      <p className="truncate text-[11px] text-codialis-gray">
                        {ligne.seance}
                        {ligne.motifJustification ? ` · ${ligne.motifJustification}` : ''}
                        {ligne.signeLe
                          ? ` · signé à ${new Date(ligne.signeLe).toLocaleTimeString('fr-FR', {
                              timeZone: 'Indian/Reunion',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}`
                          : ''}
                      </p>
                    </div>
                  </div>
                  <Pastille
                    texte={
                      ligne.statut === 'ABSENT' && ligne.justifiee
                        ? 'Absent · justifié'
                        : ligne.statutLibelle
                    }
                    ton={ligne.statutTon}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Carte>
  );
}
